import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaCalendarAlt, FaEdit, FaDownload, FaImage, FaVideo, FaHeart } from 'react-icons/fa';
import { wishesService } from '../services/wishesService';
import './PersonalizePage.css';

const PersonalizePage = () => {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Get selected date from URL params or location state
    const urlParams = new URLSearchParams(location.search);
    const dateParam = urlParams.get('date');
    const stateDate = location.state?.selectedDate;
    
    const targetDate = dateParam || stateDate;
    if (targetDate) {
      setSelectedDate(targetDate);
      fetchMediaForDate(targetDate);
    } else {
      // If no date specified, show today's date
      const today = new Date().toISOString().split('T')[0];
      setSelectedDate(today);
      fetchMediaForDate(today);
    }
  }, [location]);

  const fetchMediaForDate = async (date) => {
    try {
      setLoading(true);
      const response = await wishesService.getWishesGallery({ date });

      // Helper function to build proper URLs
      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5001';
      const buildUrl = (url) => {
        if (!url) return null;
        // If URL already starts with http/https, return as-is (Cloudinary URLs)
        if (url.startsWith('http://') || url.startsWith('https://')) {
          return url;
        }
        // Otherwise prepend baseURL for local server URLs
        return `${baseURL}${url}`;
      };

      const mediaWithUrls = response.data.map(item => ({
        ...item,
        mediaUrl: buildUrl(item.mediaUrl),
        thumbnailUrl: buildUrl(item.thumbnailUrl)
      }));

      setMedia(mediaWithUrls);
    } catch (error) {
      console.error('Error fetching media for date:', error);
      setMedia([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (item) => {
    try {
      await wishesService.incrementDownloadCount(item._id);
      // Create download link
      const link = document.createElement('a');
      link.href = item.mediaUrl;
      link.download = `${item.occasionName.replace(/\s+/g, '_')}_${item.mediaType}`;
      link.click();
    } catch (error) {
      console.error('Error downloading media:', error);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  if (loading) {
    return (
      <div className="personalize-page">
        <div className="mobile-header">
          <button className="mobile-back-btn" onClick={() => navigate(-1)}>
            <FaArrowLeft />
          </button>
          <h1 className="mobile-title">Loading...</h1>
        </div>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading personalized content...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="personalize-page">
      {/* Mobile Native Header */}
      <div className="mobile-header">
        <button className="mobile-back-btn" onClick={() => navigate(-1)}>
          <FaArrowLeft />
        </button>
        <h1 className="mobile-title">Your Content</h1>
        <div className="header-spacer"></div>
      </div>

      {/* Date Info Section */}
      <div className="date-info-section">
        <div className="date-icon">
          <FaCalendarAlt />
        </div>
        <div className="date-content">
          <p className="date-label">Content for</p>
          <h2 className="date-value">{formatDate(selectedDate)}</h2>
        </div>
        <div className="content-count">
          <span className="count-badge">{media.length}</span>
        </div>
      </div>

      {media.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">
            <FaHeart />
          </div>
          <h3>No content for this date</h3>
          <p>There are no special wishes or media available for {formatDate(selectedDate)}.</p>
        </div>
      ) : (
        <div className="content-container">
          <div className="media-list">
            {media.map((item, index) => (
              <div key={item._id} className="media-item">
                {/* Media Preview */}
                <div className="media-preview-container">
                  <div className="media-type-badge">
                    {item.mediaType === 'image' ? <FaImage /> : <FaVideo />}
                  </div>
                  {item.mediaType === 'image' ? (
                    <img
                      src={item.thumbnailUrl || item.mediaUrl}
                      alt={item.occasionName}
                      className="media-thumbnail"
                    />
                  ) : (
                    <div className="video-thumbnail-container">
                      <img
                        src={item.thumbnailUrl}
                        alt={item.occasionName}
                        className="media-thumbnail"
                      />
                      <div className="play-overlay">
                        <div className="play-button">▶</div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Media Info */}
                <div className="media-details">
                  <div className="media-header">
                    <h3 className="media-title">{item.occasionName}</h3>
                    <span className="media-category-badge">{item.category}</span>
                  </div>
                  
                  {item.tags && item.tags.length > 0 && (
                    <div className="media-tags-container">
                      {item.tags.slice(0, 3).map((tag, tagIndex) => (
                        <span key={tagIndex} className="media-tag">#{tag}</span>
                      ))}
                      {item.tags.length > 3 && (
                        <span className="more-tags">+{item.tags.length - 3}</span>
                      )}
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="action-buttons">
                    <button 
                      className="action-btn edit-btn"
                      onClick={() => navigate(`/media-editor/${item._id}`)}
                    >
                      <FaEdit />
                      <span>Edit</span>
                    </button>
                    <button
                      className="action-btn download-btn"
                      onClick={() => handleDownload(item)}
                    >
                      <FaDownload />
                      <span>Save</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default PersonalizePage;